import { zodResolver } from "@hookform/resolvers/zod";
import {
  ROLE_LABELS,
  createClientSchema,
  roleCanBeRestrictedToClients,
  roleHas,
  type ClientDto,
  type CreateClientInput,
  type MemberDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Link2, MoreHorizontal, Plus, Target, Users } from "lucide-react";
import { useMemo, useState } from "react";
import type { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { ClientDot } from "@/components/client-filter";
import { ClientGoalsDialog } from "@/components/client-goals-dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";

export const Route = createFileRoute("/app/$workspaceId/clientes")({
  component: ClientesPage,
});

function ClientesPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));
  const clients = useQuery(queries.clients(workspaceId));
  const [novo, setNovo] = useState(false);
  const podeGerenciar = workspace.data ? roleHas(workspace.data.role, "clients:manage") : false;

  return (
    <>
      <PageHeader
        title="Clientes"
        description="Empresas atendidas pela agência. Cada cliente agrupa Business Managers, contas de anúncio e páginas, e define o que cada membro restrito enxerga."
        actions={
          podeGerenciar ? (
            <Button onClick={() => setNovo(true)}>
              <Plus />
              Novo cliente
            </Button>
          ) : null
        }
      />
      <Card>
        <CardContent>
          {clients.isPending ? (
            <LoadingState rows={3} />
          ) : clients.isError ? (
            <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
          ) : clients.data.length === 0 ? (
            <EmptyState
              icon={<Building2 className="size-8" aria-hidden />}
              title="Nenhum cliente"
              description={
                podeGerenciar
                  ? "Crie o primeiro cliente e vincule os BMs, contas e páginas dele."
                  : "Nenhum cliente foi cadastrado ou vinculado a você ainda."
              }
              action={
                podeGerenciar ? (
                  <Button onClick={() => setNovo(true)}>
                    <Plus />
                    Novo cliente
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y">
              {clients.data.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  workspaceId={workspaceId}
                  podeGerenciar={podeGerenciar}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <ClientFormDialog open={novo} onOpenChange={setNovo} workspaceId={workspaceId} />
    </>
  );
}

function ClientRow({
  client,
  workspaceId,
  podeGerenciar,
}: {
  client: ClientDto;
  workspaceId: string;
  podeGerenciar: boolean;
}) {
  const queryClient = useQueryClient();
  const [editar, setEditar] = useState(false);
  const [ativos, setAtivos] = useState(false);
  const [membros, setMembros] = useState(false);
  const [metas, setMetas] = useState(false);
  const [excluir, setExcluir] = useState(false);
  const remover = useMutation({
    mutationFn: () =>
      api(`/api/workspaces/${workspaceId}/clients/${client.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setExcluir(false);
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId] });
      toast.success("Cliente excluído.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const total =
    client.businessMetaIds.length + client.adAccountMetaIds.length + client.pageMetaIds.length;

  return (
    <li className="flex items-start gap-3 py-4">
      <span
        aria-hidden
        className="mt-1 size-4 shrink-0 rounded-full"
        style={{ backgroundColor: client.color }}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium">{client.name}</p>
        <p className="text-xs text-muted-foreground">
          {client.businessMetaIds.length} BM · {client.adAccountMetaIds.length} contas ·{" "}
          {client.pageMetaIds.length} páginas · {client.memberIds.length}{" "}
          {client.memberIds.length === 1 ? "membro restrito" : "membros restritos"}
        </p>
        {client.notes ? (
          <p className="text-sm whitespace-pre-line text-muted-foreground">{client.notes}</p>
        ) : null}
      </div>
      {total === 0 ? <Badge variant="secondary">Sem ativos</Badge> : null}
      {podeGerenciar ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Ações para ${client.name}`}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setAtivos(true)}>
              <Link2 />
              Vincular ativos
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setMembros(true)}>
              <Users />
              Vincular membros
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setMetas(true)}>
              <Target />
              Metas (CPA, ROAS, orçamento)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setEditar(true)}>Editar</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => setExcluir(true)}>
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <ClientFormDialog
        open={editar}
        onOpenChange={setEditar}
        workspaceId={workspaceId}
        client={client}
      />
      <AssetsDialog
        open={ativos}
        onOpenChange={setAtivos}
        workspaceId={workspaceId}
        client={client}
      />
      <MembersDialog
        open={membros}
        onOpenChange={setMembros}
        workspaceId={workspaceId}
        client={client}
      />
      <ClientGoalsDialog
        open={metas}
        onOpenChange={setMetas}
        workspaceId={workspaceId}
        client={client}
      />
      <ConfirmDialog
        open={excluir}
        onOpenChange={setExcluir}
        title={`Excluir ${client.name}?`}
        description="Os BMs, contas e páginas continuam no workspace, só deixam de pertencer a este cliente. Membros restritos a ele perdem o acesso a esses ativos."
        confirmLabel="Excluir cliente"
        destructive
        loading={remover.isPending}
        onConfirm={() => remover.mutate()}
      />
    </li>
  );
}

function ClientFormDialog({
  open,
  onOpenChange,
  workspaceId,
  client,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client?: ClientDto;
}) {
  // Montado só enquanto aberto: o formulário nasce com os valores atuais a cada abertura.
  if (!open) return null;
  return (
    <ClientFormDialogOpen
      onOpenChange={onOpenChange}
      workspaceId={workspaceId}
      {...(client ? { client } : {})}
    />
  );
}

type ClientForm = z.input<typeof createClientSchema>;

function ClientFormDialogOpen({
  onOpenChange,
  workspaceId,
  client,
}: {
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client?: ClientDto;
}) {
  const queryClient = useQueryClient();
  const form = useForm<ClientForm, unknown, CreateClientInput>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      name: client?.name ?? "",
      color: client?.color ?? "#2563eb",
      notes: client?.notes ?? "",
    },
  });
  const color = useWatch({ control: form.control, name: "color" });

  const salvar = useMutation({
    mutationFn: (input: CreateClientInput) =>
      client
        ? api<ClientDto>(`/api/workspaces/${workspaceId}/clients/${client.id}`, {
            method: "PATCH",
            body: input,
          })
        : api<ClientDto>(`/api/workspaces/${workspaceId}/clients`, { method: "POST", body: input }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId] });
      toast.success(client ? "Cliente atualizado." : `Cliente ${saved.name} criado.`);
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => salvar.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{client ? "Editar cliente" : "Novo cliente"}</DialogTitle>
            <DialogDescription>
              Nome, cor de identificação e observações internas da agência.
            </DialogDescription>
          </DialogHeader>
          <FormField id="client-name" label="Nome" error={form.formState.errors.name?.message}>
            <Input
              id="client-name"
              autoFocus
              placeholder="Ex.: Bella Moda"
              {...form.register("name")}
            />
          </FormField>
          <FormField id="client-color" label="Cor" error={form.formState.errors.color?.message}>
            <div className="flex items-center gap-2">
              <input
                id="client-color"
                type="color"
                className="size-9 cursor-pointer rounded-md border bg-transparent p-1"
                value={color}
                onChange={(e) => form.setValue("color", e.target.value, { shouldValidate: true })}
              />
              <Input
                aria-label="Cor em hexadecimal"
                className="w-32 font-mono"
                {...form.register("color")}
              />
            </div>
          </FormField>
          <FormField
            id="client-notes"
            label="Observações"
            error={form.formState.errors.notes?.message}
            hint="Só a equipe vê. Contatos, combinados, particularidades."
          >
            <Textarea id="client-notes" rows={3} {...form.register("notes")} />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={salvar.isPending}>
              {client ? "Salvar" : "Criar cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CheckItem {
  id: string;
  label: string;
  subtitle?: string | null;
  takenBy?: string | null;
}

function CheckList({
  title,
  items,
  selected,
  onToggle,
  emptyText,
}: {
  title: string;
  items: CheckItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  emptyText: string;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{title}</legend>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
          {items.map((item) => (
            <li key={item.id}>
              <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-primary"
                  aria-label={item.label}
                  checked={selected.has(item.id)}
                  onChange={() => onToggle(item.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{item.label}</span>
                  {item.subtitle ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.subtitle}
                    </span>
                  ) : null}
                  {item.takenBy ? (
                    <span className="block text-xs text-amber-700 dark:text-amber-400">
                      Hoje em: {item.takenBy} (vai mudar para este cliente)
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

/** Seleção de ids; o diálogo é montado a cada abertura, então o estado inicial já é o atual. */
function useSelection(initial: string[]) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial));
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return [selected, toggle] as const;
}

function AssetsDialog({
  open,
  onOpenChange,
  workspaceId,
  client,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client: ClientDto;
}) {
  if (!open) return null;
  return <AssetsDialogOpen onOpenChange={onOpenChange} workspaceId={workspaceId} client={client} />;
}

function AssetsDialogOpen({
  onOpenChange,
  workspaceId,
  client,
}: {
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client: ClientDto;
}) {
  const queryClient = useQueryClient();
  const businesses = useQuery(queries.businesses(workspaceId));
  const accounts = useQuery(queries.adAccounts(workspaceId));
  const pages = useQuery(queries.pages(workspaceId));
  const clients = useQuery(queries.clients(workspaceId));
  const [bms, toggleBm] = useSelection(client.businessMetaIds);
  const [accs, toggleAcc] = useSelection(client.adAccountMetaIds);
  const [pgs, togglePg] = useSelection(client.pageMetaIds);

  const takenBy = (clientId: string | null) =>
    clientId && clientId !== client.id
      ? (clients.data?.find((c) => c.id === clientId)?.name ?? null)
      : null;

  const salvar = useMutation({
    mutationFn: () =>
      api<ClientDto>(`/api/workspaces/${workspaceId}/clients/${client.id}/assets`, {
        method: "PUT",
        body: {
          businessMetaIds: [...bms],
          adAccountMetaIds: [...accs],
          pageMetaIds: [...pgs],
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId] });
      toast.success("Ativos vinculados.");
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const loading = businesses.isPending || accounts.isPending || pages.isPending;
  const error = businesses.error ?? accounts.error ?? pages.error;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <ClientDot client={client} />
              Ativos de {client.name}
            </span>
          </DialogTitle>
          <DialogDescription>
            Marque os Business Managers, contas de anúncio e páginas deste cliente. Um ativo
            pertence a um único cliente.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <LoadingState rows={4} />
        ) : error ? (
          <ErrorState error={error} />
        ) : (
          <div className="space-y-5">
            <CheckList
              title="Business Managers"
              items={(businesses.data ?? []).map((b) => ({
                id: b.metaId,
                label: b.name,
                subtitle: `id ${b.metaId}`,
                takenBy: takenBy(b.clientId),
              }))}
              selected={bms}
              onToggle={toggleBm}
              emptyText="Nenhum BM sincronizado."
            />
            <CheckList
              title="Contas de anúncio"
              items={(accounts.data ?? []).map((a) => ({
                id: a.metaId,
                label: a.name,
                subtitle: `${a.metaId} · ${a.ownerBusinessName ?? "sem BM"}`,
                takenBy: takenBy(a.clientId),
              }))}
              selected={accs}
              onToggle={toggleAcc}
              emptyText="Nenhuma conta sincronizada."
            />
            <CheckList
              title="Páginas"
              items={(pages.data ?? []).map((p) => ({
                id: p.metaId,
                label: p.name,
                subtitle: p.instagramUsername ? `@${p.instagramUsername}` : `id ${p.metaId}`,
                takenBy: takenBy(p.clientId),
              }))}
              selected={pgs}
              onToggle={togglePg}
              emptyText="Nenhuma página sincronizada."
            />
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => salvar.mutate()}
            loading={salvar.isPending}
            disabled={loading}
          >
            Salvar vínculos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({
  open,
  onOpenChange,
  workspaceId,
  client,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client: ClientDto;
}) {
  if (!open) return null;
  return (
    <MembersDialogOpen onOpenChange={onOpenChange} workspaceId={workspaceId} client={client} />
  );
}

function MembersDialogOpen({
  onOpenChange,
  workspaceId,
  client,
}: {
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  client: ClientDto;
}) {
  const queryClient = useQueryClient();
  const members = useQuery(queries.members(workspaceId));
  const [selected, toggle] = useSelection(client.memberIds);
  const restrictable = useMemo(
    () => (members.data ?? []).filter((m: MemberDto) => roleCanBeRestrictedToClients(m.role)),
    [members.data],
  );
  const salvar = useMutation({
    mutationFn: () =>
      api<ClientDto>(`/api/workspaces/${workspaceId}/clients/${client.id}/members`, {
        method: "PUT",
        body: { memberIds: [...selected] },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId] });
      toast.success("Membros vinculados.");
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Membros de {client.name}</DialogTitle>
          <DialogDescription>
            Gestores e Analistas marcados aqui passam a ver só os clientes aos quais estão
            vinculados (sem vínculo nenhum, veem tudo). O Cliente final só vê os clientes
            vinculados. Dono e Admin sempre veem tudo.
          </DialogDescription>
        </DialogHeader>
        {members.isPending ? (
          <LoadingState rows={3} />
        ) : members.isError ? (
          <ErrorState error={members.error} />
        ) : (
          <CheckList
            title="Membros"
            items={restrictable.map((m) => ({
              id: m.id,
              label: m.name,
              subtitle: `${m.email} · ${ROLE_LABELS[m.role]}`,
            }))}
            selected={selected}
            onToggle={toggle}
            emptyText="Nenhum Gestor, Analista ou Cliente final no workspace."
          />
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => salvar.mutate()} loading={salvar.isPending}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
